import { PreOrderType, SpecialMenuCard } from 'src/gql/graphql';

export type SpecialFilterKey = 'all' | 'bogo' | 'bundles' | 'delivery' | 'ending-soon' | 'everyday';

type SpecialFilterLabel =
  | 'All Sales & Offers'
  | 'BOGO'
  | 'Bundles'
  | 'Delivery Specials'
  | 'Ending Soon'
  | 'Everyday Deals';
type ComplianceFilterLabel = SpecialFilterLabel | 'All Offers' | 'Delivery Offers';

type FilterLabel = {
  label: SpecialFilterLabel;
  complianceLabel?: ComplianceFilterLabel;
};

const ENDING_SOON_THRESHOLD_MS = 48 * 60 * 60 * 1000;

export const CHIP_LABELS: Record<SpecialFilterKey, FilterLabel> = {
  all: { label: 'All Sales & Offers', complianceLabel: 'All Offers' },
  bogo: { label: 'BOGO', complianceLabel: 'BOGO' },
  bundles: { label: 'Bundles', complianceLabel: 'Bundles' },
  delivery: { label: 'Delivery Specials', complianceLabel: 'Delivery Offers' },
  'ending-soon': { label: 'Ending Soon', complianceLabel: 'Ending Soon' },
  everyday: { label: 'Everyday Deals', complianceLabel: 'Everyday Deals' },
} as const;

/**
 * Filter keys eligible for the Sales & Offers filter UI (menu sidebar filter panel and its SDK bridge).
 *
 * Reduced to a single safe option after the specials-redesign BOGO over-matching incident: the
 * `bogo`/`bundles`/`everyday`/`ending-soon`/`delivery` signals are either unreliable (POS-synced
 * dispensaries tag plain quantity-break deals as `specialType === 'bogo'`) or gated on data that
 * isn't consistently available. See ecomm.specials-redesign.rollout incident follow-up.
 */
export const FILTERABLE_KEYS: SpecialFilterKey[] = ['all'];

/** Resolves the display label for a special filter key, swapping to compliance wording when needed. */
export function getFilterLabel(key: SpecialFilterKey, hideSalesLanguage: boolean): string {
  const entry = CHIP_LABELS[key];
  return hideSalesLanguage && entry.complianceLabel ? entry.complianceLabel : entry.label;
}

export function isSpecialFilterKey(value: string): value is SpecialFilterKey {
  return value in CHIP_LABELS;
}

export function toggleSpecialFilter(current: string[], value: string): string[] {
  if (value === 'all') {
    return current.includes('all') ? [] : ['all'];
  }
  const withoutAll = current.filter((currentValue) => currentValue !== 'all');
  return withoutAll.includes(value)
    ? withoutAll.filter((currentValue) => currentValue !== value)
    : [...withoutAll, value];
}

export function createSpecialMatcher(key: SpecialFilterKey): ((special: SpecialMenuCard) => boolean) | null {
  switch (key) {
    case 'all':
      return null;
    case 'bogo':
      return (s) => s.specialType === 'bogo';
    case 'bundles':
      return (s) => s.isBundledDiscount === true;
    case 'delivery':
      return (s) => (s.applicablePreOrderTypes ?? []).includes(PreOrderType.delivery);
    case 'ending-soon':
      return (s) => {
        if (!s.endStamp) return false;
        const endTime = new Date(s.endStamp).getTime();
        const now = Date.now();
        return endTime > now && endTime - now <= ENDING_SOON_THRESHOLD_MS;
      };
    case 'everyday':
      return (s) => s.recurringSchedule != null;
    default:
      return null;
  }
}

/** Whether at least one special in the given list qualifies for this filter key. */
export function specialsMatchFilterKey(key: SpecialFilterKey, specials: SpecialMenuCard[]): boolean {
  if (key === 'all') {
    return specials.length > 0;
  }
  const matcher = createSpecialMatcher(key);
  return matcher != null && specials.some(matcher);
}
